import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { CsvExportButton } from '../../components/data/CsvExportButton.jsx';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { formatDate } from '../../utils/formatters.js';
import { TimeAccountCard } from '../self/TimeAccountCard.jsx';

const TYPES = ['REGULAR', 'OVERTIME', 'SICK', 'HOLIDAY', 'TRAINING'];
const TYPE_VARIANT = { REGULAR: 'info', OVERTIME: 'warning', SICK: 'danger', HOLIDAY: 'neutral', TRAINING: 'success' };

function monthRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const last = new Date(y, now.getMonth() + 1, 0).getDate();
  return { from: `${y}-${m}-01`, to: `${y}-${m}-${String(last).padStart(2, '0')}` };
}

/**
 * Der Tag, den das Erfassungsfeld vorschlaegt.
 *
 * HEUTE, nicht das Ende des Zeitraums. Vorher stand hier `range.to` - der
 * LETZTE Tag des Monats. Am 28. August bot die Maske also den 31. August an,
 * ein Datum, das noch gar nicht stattgefunden hat. Wer nicht hinsah, buchte
 * die Stunden von heute auf einen Tag in der Zukunft; im Wochenraster fehlten
 * sie dann dort, wo man sie suchte.
 */
function heuteIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function TimeTrackingPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const range = useMemo(monthRange, []);
  const [filter, setFilter] = useState({ employeeId: '', from: range.from, to: range.to });
  const [entry, setEntry] = useState({ employeeId: '', workDate: heuteIso(), hours: '8', type: 'REGULAR', note: '' });
  const [error, setError] = useState(null);

  const employees = useQuery({ queryKey: ['hr', 'employees'], queryFn: () => hrApi.listEmployees({ limit: 200 }).then((r) => r.items) });

  const params = { ...(filter.employeeId ? { employeeId: filter.employeeId } : {}), from: filter.from, to: filter.to };
  /* limit ausdrücklich mitschicken. Bisher wurde es wegen stripUnknown
     still verworfen, und die Monatsansicht bekam JEDE gefundene Zeile - bei
     zwanzig Personen rund 5000 im Jahr. */
  const list = useQuery({
    queryKey: ['hr', 'time', params],
    queryFn: () => hrApi.listTimeEntries({ ...params, limit: 500 }),
  });
  const summary = useQuery({ queryKey: ['hr', 'time-summary', params], queryFn: () => hrApi.timeSummary(params).then((r) => r.items) });
  /* Das Stundenkonto gibt es nur je Person - eine Summe über alle wäre eine
     Zahl ohne Bedeutung. Deshalb hängt es am Filter und nicht am Zeitraum:
     der Kontostand gilt bis heute, egal welchen Monat man gerade ansieht. */
  const konto = useQuery({
    queryKey: ['hr', 'time-account', filter.employeeId],
    queryFn: () => hrApi.timeAccount(filter.employeeId),
    enabled: Boolean(filter.employeeId),
  });
  const rows = list.data?.items || [];
  const gesamt = list.data?.meta?.total;
  const abgeschnitten = gesamt !== undefined && gesamt > rows.length;
  const byType = summary.data?.byType || [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['hr', 'time'] });
    queryClient.invalidateQueries({ queryKey: ['hr', 'time-summary'] });
    // Jede erfasste Stunde ändert das Konto. Ein Kontostand, der erst nach
    // einem Neuladen stimmt, ist schlimmer als keiner.
    queryClient.invalidateQueries({ queryKey: ['hr', 'time-account'] });
  };
  const add = useMutation({
    mutationFn: (body) => hrApi.createTimeEntry(body),
    onSuccess: () => { setError(null); setEntry((e) => ({ ...e, note: '' })); invalidate(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });
  const del = useMutation({ mutationFn: (id) => hrApi.deleteTimeEntry(id), onSuccess: invalidate });

  /* Der Anfangsbestand. Wer dieses System einführt, bringt bestehende
     Ueberstunden mit; ohne ihn begänne jedes Konto bei null, und die ersten
     Monate wären eine Diskussion darüber, was vorher war.

     Er steht HIER und nicht in der Personalakte: gebraucht wird er genau in
     dem Moment, in dem jemand auf ein Stundenkonto schaut und sich wundert. */
  const [anfang, setAnfang] = useState({ offen: false, hours: '', start: '' });
  const anfangSetzen = useMutation({
    mutationFn: () =>
      hrApi.updateEmployee(filter.employeeId, {
        timeAccountOpeningHours: Number(anfang.hours || 0),
        ...(anfang.start ? { timeAccountStart: anfang.start } : {}),
      }),
    onSuccess: () => {
      setError(null);
      setAnfang({ offen: false, hours: '', start: '' });
      queryClient.invalidateQueries({ queryKey: ['hr', 'time-account'] });
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!entry.employeeId || !entry.workDate || !entry.hours) { setError(t('hrtime.error.required')); return; }
    add.mutate({ employeeId: entry.employeeId, workDate: entry.workDate, hours: Number(entry.hours), type: entry.type, note: entry.note || null });
  }

  /**
   * Die Spalten der Ausfuhr - übersetzt, nicht englisch fest verdrahtet.
   *
   * Bewusst NICHT dieselbe Liste wie die Tabellenspalten: dort stehen
   * Abzeichen und Knöpfe, hier gehören Rohwerte hin. Und der Treuhänder
   * will den Mitarbeiter und die Art ausgeschrieben, nicht als Farbe.
   */
  const csvSpalten = useMemo(
    () => [
      { key: 'workDate', label: t('hrtime.col.date') },
      { key: 'employeeName', label: t('hrtime.col.employee') },
      { key: 'type', label: t('hrtime.col.type'), map: (r) => t(`hrtime.type.${r.type}`) },
      { key: 'hours', label: t('hrtime.col.hours'), map: (r) => Number(r.hours).toFixed(2) },
      { key: 'startTime', label: t('time.add.start'), map: (r) => r.startTime || '' },
      { key: 'endTime', label: t('time.add.end'), map: (r) => r.endTime || '' },
      { key: 'breakMinutes', label: t('time.add.break'), map: (r) => r.breakMinutes ?? 0 },
      { key: 'note', label: t('hrtime.col.note'), map: (r) => r.note || '' },
    ],
    [t],
  );

  /* Der Zeitraum gehört in den Dateinamen. Ohne ihn heissen drei Ausfuhren
     zeiterfassung.csv, zeiterfassung (1).csv und zeiterfassung (2).csv, und
     niemand weiss hinterher, welche welchen Monat enthält. */
  const dateiname = `zeiterfassung_${filter.from}_${filter.to}`;

  const columns = [
    { key: 'date', label: t('hrtime.col.date'), render: (r) => formatDate(r.workDate) },
    { key: 'employee', label: t('hrtime.col.employee'), render: (r) => r.employeeName },
    { key: 'type', label: t('hrtime.col.type'), render: (r) => <Badge variant={TYPE_VARIANT[r.type]}>{t(`hrtime.type.${r.type}`)}</Badge> },
    { key: 'hours', label: t('hrtime.col.hours'), align: 'right', render: (r) => <span className="mono num">{Number(r.hours).toFixed(2)}</span> },
    { key: 'note', label: t('hrtime.col.note'), render: (r) => r.note || '-' },
    { key: 'actions', label: '', align: 'right', render: (r) => <Button size="sm" variant="ghost" disabled={del.isPending} onClick={() => del.mutate(r.id)}>{t('hrtime.action.delete')}</Button> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('hrtime.title')}</h1>
          <p className="muted">{t('hrtime.subtitle')}</p>
        </div>
        <div className="page-actions">
          {/* Für den Treuhänder und für eine Kontrolle. Bisher musste
              dafür jemand abschreiben. */}
          {/* Abgeschnitten wird NICHT ausgeführt. Der Dateiname trägt den
              ganzen Zeitraum; eine Datei, die davon nur die ersten 500
              Zeilen enthält, wäre für den Treuhänder schlimmer als gar
              keine - sie sieht vollständig aus. */}
          {abgeschnitten ? (
            <Button variant="default" disabled title={t('hrtime.exportBlocked')}>
              ↓ {t('csv.export')}
            </Button>
          ) : (
            <CsvExportButton rows={rows} columns={csvSpalten} filename={dateiname} />
          )}
          <Link
            className="btn"
            to={`/hr/time/report?from=${filter.from}&to=${filter.to}${
              filter.employeeId ? `&employeeId=${filter.employeeId}` : ''
            }`}
          >
            {t('hrtime.report.open')}
          </Link>
        </div>
      </header>

      <form onSubmit={submit} className="card" style={{ padding: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
        <div className="field-row" style={{ alignItems: 'flex-end', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
          <label className="field"><span className="field-label">{t('hrtime.field.employee')}</span>
            <select className="input" value={entry.employeeId} onChange={(e) => setEntry((s) => ({ ...s, employeeId: e.target.value }))} required>
              <option value="">-</option>
              {(employees.data || []).map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>)}
            </select>
          </label>
          <label className="field"><span className="field-label">{t('hrtime.field.date')}</span>
            <input className="input" type="date" value={entry.workDate} onChange={(e) => setEntry((s) => ({ ...s, workDate: e.target.value }))} required />
          </label>
          <label className="field"><span className="field-label">{t('hrtime.field.hours')}</span>
            <input className="input num" type="number" min="0.25" max="24" step="0.25" value={entry.hours} onChange={(e) => setEntry((s) => ({ ...s, hours: e.target.value }))} required />
          </label>
          <label className="field"><span className="field-label">{t('hrtime.field.type')}</span>
            <select className="input" value={entry.type} onChange={(e) => setEntry((s) => ({ ...s, type: e.target.value }))}>
              {TYPES.map((ty) => <option key={ty} value={ty}>{t(`hrtime.type.${ty}`)}</option>)}
            </select>
          </label>
          <label className="field" style={{ flex: 1 }}><span className="field-label">{t('hrtime.field.note')}</span>
            <input className="input" value={entry.note} onChange={(e) => setEntry((s) => ({ ...s, note: e.target.value }))} />
          </label>
          <Button variant="primary" type="submit" disabled={add.isPending}>{t('hrtime.action.add')}</Button>
        </div>
        {error && <div className="alert danger" style={{ marginTop: 'var(--space-2)' }}>{error}</div>}
      </form>

      <div className="field-row" style={{ marginBottom: 'var(--space-3)', flexWrap: 'wrap' }}>
        <label className="field"><span className="field-label">{t('hrtime.filter.employee')}</span>
          <select className="input" value={filter.employeeId} onChange={(e) => setFilter((f) => ({ ...f, employeeId: e.target.value }))}>
            <option value="">{t('hrtime.filter.all')}</option>
            {(employees.data || []).map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>)}
          </select>
        </label>
        <label className="field"><span className="field-label">{t('hrtime.field.from')}</span>
          <input className="input" type="date" value={filter.from} onChange={(e) => setFilter((f) => ({ ...f, from: e.target.value }))} />
        </label>
        <label className="field"><span className="field-label">{t('hrtime.field.to')}</span>
          <input className="input" type="date" value={filter.to} onChange={(e) => setFilter((f) => ({ ...f, to: e.target.value }))} />
        </label>
      </div>

      {/* Nur bei EINER Person. Ein Stundenkonto über alle wäre eine Summe,
          die niemandem gehört - und die man trotzdem lesen würde, als
          gehörte sie jemandem. */}
      {filter.employeeId && (
        <>
          <TimeAccountCard account={konto.data} loading={konto.isLoading} />
          {anfang.offen ? (
            <form
              className="card"
              style={{ padding: 'var(--space-3)', marginBottom: 'var(--space-4)' }}
              onSubmit={(e) => {
                e.preventDefault();
                anfangSetzen.mutate();
              }}
            >
              <p className="muted text-sm" style={{ marginTop: 0 }}>{t('timeAccount.openingHint')}</p>
              <div className="field-row" style={{ alignItems: 'flex-end', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
                <label className="field" style={{ maxWidth: '10rem' }}>
                  <span className="field-label">{t('timeAccount.openingField')}</span>
                  <input
                    className="input num"
                    type="number"
                    step="0.25"
                    value={anfang.hours}
                    onChange={(e) => setAnfang((p) => ({ ...p, hours: e.target.value }))}
                    required
                  />
                </label>
                <label className="field" style={{ maxWidth: '12rem' }}>
                  <span className="field-label">{t('timeAccount.startField')}</span>
                  <input
                    className="input"
                    type="date"
                    value={anfang.start}
                    onChange={(e) => setAnfang((p) => ({ ...p, start: e.target.value }))}
                  />
                </label>
                <Button variant="primary" type="submit" disabled={anfangSetzen.isPending}>
                  {t('common.save')}
                </Button>
                <Button variant="ghost" type="button" onClick={() => setAnfang({ offen: false, hours: '', start: '' })}>
                  {t('common.cancel')}
                </Button>
              </div>
            </form>
          ) : (
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setAnfang({
                    offen: true,
                    hours: String(konto.data?.openingHours ?? 0),
                    start: (konto.data?.startsAt || '').slice(0, 10),
                  })
                }
              >
                {t('timeAccount.setOpening')}
              </Button>
            </div>
          )}
        </>
      )}

      <div className="card-grid">
        <MetricCard label={t('hrtime.metric.total')} value={`${summary.data?.total || '0.00'} h`} />
        {byType.map((b) => <MetricCard key={b.type} label={t(`hrtime.type.${b.type}`)} value={`${Number(b.hours).toFixed(2)} h`} />)}
      </div>

      {/* Wenn die Grenze greift, MUSS das dastehen. Eine stillschweigend
          abgeschnittene Liste, über der eine Gesamtzahl steht, ist genau
          die Art Halbwahrheit, die niemand bemerkt. */}
      {abgeschnitten && (
        <div className="alert warning">{t('hrtime.truncated', { shown: rows.length, total: gesamt })}</div>
      )}

      <Table columns={columns} rows={rows} isLoading={list.isLoading} emptyMessage={t('hrtime.empty')} />
    </section>
  );
}
